import { supabase } from './supabase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const COLLECTION = process.env.REACT_APP_COLLECTION || null;

/**
 * Call the read-api edge function (rate-limited proxy for all read operations).
 */
async function readApi(action, params = {}) {
  // Include user token if logged in (edge function uses it for collection access control)
  const { data: { session } } = await supabase.auth.getSession();
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
  };
  if (session?.access_token) {
    headers['x-user-token'] = session.access_token;
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/read-api`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, params }),
  });

  if (res.status === 429) {
    throw new Error('Too many requests — please slow down');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Edge function error');
  }

  return res.json();
}

/**
 * Fetch items within geographic bounds.
 * Replaces: API.get(`items/filter/${_ne}/${_sw}/`)
 */
export async function fetchItemsInBounds(ne, sw) {
  const [neLng, neLat] = ne.split(',').map(Number);
  const [swLng, swLat] = sw.split(',').map(Number);

  const data = await readApi('items_in_bounds', {
    ne_lng: neLng, ne_lat: neLat,
    sw_lng: swLng, sw_lat: swLat,
    collection: COLLECTION
  });

  return data?.features || [];
}

/**
 * Search items by text.
 * @param {string[]=} creators  Optional creator filter (canonical English names matching media_items.creator).
 *                              Empty array or undefined = no filter.
 */
export async function searchItems(queryText, creators) {
  const params = { query_text: queryText, collection: COLLECTION };
  if (Array.isArray(creators) && creators.length > 0) params.creators = creators;
  const data = await readApi('search_items', params);
  return data?.features || [];
}

/**
 * Search items by geotagger name.
 */
export async function searchByGeotagger(geotaggerName, creators) {
  const params = { geotagger_name: geotaggerName, collection: COLLECTION };
  if (Array.isArray(creators) && creators.length > 0) params.creators = creators;
  const data = await readApi('search_by_geotagger', params);
  return data?.features || [];
}

/**
 * Fetch homeless (unlocated) items.
 * @param {boolean} includeUnfit - If true, return only unfit items; if false, exclude them
 * @param {number} offset
 * @param {number} limit
 * @param {string[]=} creators - optional creator filter
 */
export async function fetchHomelessItems(includeUnfit = false, offset = 0, limit = 60, creators) {
  const params = { collection: COLLECTION, include_unfit: includeUnfit, offset, limit };
  if (Array.isArray(creators) && creators.length > 0) params.creators = creators;
  const data = await readApi('homeless_items', params);
  return data?.features || [];
}

export async function fetchHomelessCount(includeUnfit = false, creators) {
  const params = { collection: COLLECTION, include_unfit: includeUnfit };
  if (Array.isArray(creators) && creators.length > 0) params.creators = creators;
  const data = await readApi('homeless_items_count', params);
  return typeof data === 'number' ? data : 0;
}

/**
 * Fetch distinct sources.
 * Replaces: API.get('items/sources/')
 */
export async function fetchSources() {
  const data = await readApi('get_sources', { collection: COLLECTION });
  return data?.sources || [];
}

/**
 * Fetch distinct creators (for the unified-site creator dropdown).
 * Returns an array of { creator, creator_heb, count } sorted by count DESC.
 */
export async function getDistinctCreators() {
  const data = await readApi('get_distinct_creators', { collection: COLLECTION });
  return Array.isArray(data) ? data : [];
}

/**
 * Fetch year bounds (min/max year across DB) for the year-range slider.
 * Returns { min_year, max_year } or null if no items have years.
 */
export async function getYearBounds() {
  const data = await readApi('get_year_bounds', { collection: COLLECTION });
  return data || null;
}

/**
 * Fetch all authors.
 * Replaces: API.get('authors/')
 */
export async function fetchAuthors() {
  const data = await readApi('fetch_authors');
  return data || [];
}

/**
 * Update item coordinates and angle.
 * Replaces: API.post('items/updatecoords/', { itemid, lat, lng, angle })
 */
export async function updateCoords(itemId, lat, lng, angle, userId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Session expired — please log in again');
  }
  const params = {
    item_id: itemId,
    lat: lat,
    lng: lng,
    new_angle: Math.round(angle),
    tagged_by: userId || null
  };
  console.log('updateCoords called with:', JSON.stringify(params));
  const { data, error } = await supabase.rpc('update_coords', params);
  console.log('updateCoords response:', { data, error });
  if (error) throw error;

  // Reverse geocode to populate address (fire-and-forget, don't block the UI)
  reverseGeocodeItem(itemId, lng, lat).catch(err =>
    console.warn('Reverse geocode failed for item', itemId, err)
  );
}

/**
 * Reverse geocode coordinates and store address on the item.
 */
async function reverseGeocodeItem(itemId, lng, lat) {
  const types = 'address,neighborhood,locality,place';
  const base = 'https://api.mapbox.com/geocoding/v5/mapbox.places';
  const [enRes, heRes] = await Promise.all([
    fetch(`${base}/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&types=${types}&language=en`).then(r => r.json()),
    fetch(`${base}/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&types=${types}&language=he`).then(r => r.json()),
  ]);

  function extractParts(data) {
    const parts = [];
    for (const f of (data.features || [])) {
      if (['address', 'neighborhood', 'locality', 'place'].includes(f.place_type?.[0])) {
        parts.push(f.text);
      }
    }
    return [...new Set(parts)];
  }

  const en = extractParts(enRes).join(', ');
  const he = extractParts(heRes).join(', ');
  const address = en && he && en !== he ? `${en} | ${he}` : en || he || null;

  if (address) {
    await supabase.from('media_items').update({ address }).eq('id', itemId);
  }
}

/**
 * Delete item coordinates (make homeless).
 * Replaces: API.post('items/deletecoords/', { itemid })
 */
export async function deleteCoords(itemId) {
  const { error } = await supabase.rpc('delete_coords', {
    item_id: itemId
  });

  if (error) throw error;
}

/**
 * Update title and/or desc fields on a media item.
 */
export async function updateItemFields(itemId, fields) {
  const { error } = await supabase
    .from('media_items')
    .update(fields)
    .eq('id', itemId);
  if (error) throw error;
}

/**
 * Mark a single item as unfit (or fit) for tagging.
 */
export async function markUnfit(itemId, unfit = true) {
  const { error } = await supabase
    .from('media_items')
    .update({ unfit_for_tagging: unfit })
    .eq('id', itemId);
  if (error) throw error;
}

/**
 * Mark multiple items as unfit (or fit) for tagging.
 */
export async function markItemsUnfit(itemIds, unfit = true) {
  const { error } = await supabase
    .from('media_items')
    .update({ unfit_for_tagging: unfit })
    .in('id', itemIds);
  if (error) throw error;
}

/**
 * Toggle favorite on an item.
 * Replaces: API.post('items/updatefav/', { itemid, user })
 */
export async function toggleFav(itemId, userId) {
  const { data, error } = await supabase.rpc('toggle_fav', {
    item_id: itemId,
    fav_user_id: userId
  });

  if (error) throw error;
  return data || [];
}

/**
 * Toggle questionable flag on an item.
 */
export async function toggleQuestionable(itemId, userId) {
  const data = await readApi('toggle_questionable', {
    item_id: itemId,
    user_id: userId
  });
  return data || [];
}

/**
 * Fetch comments for an item.
 * Replaces: API.get(`comments/byitem/${itemId}/`)
 */
export async function fetchComments(itemId) {
  const data = await readApi('fetch_comments', { p_item_id: itemId });
  return data || [];
}

/**
 * Add a comment to an item.
 * Replaces: API.post('comments/add/', { text, author, item })
 */
export async function addComment(text, itemId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('comments')
    .insert({ text, item_id: itemId, author_id: user.id });

  if (error) throw error;

  return fetchComments(itemId);
}

/**
 * Delete a comment.
 */
export async function deleteComment(commentId, itemId) {
  const { error } = await supabase
    .from('comments')
    .delete()
    .eq('id', commentId);

  if (error) throw error;
  return fetchComments(itemId);
}

/**
 * Update a comment's text.
 */
export async function updateComment(commentId, newText, itemId) {
  const { error } = await supabase
    .from('comments')
    .update({ text: newText })
    .eq('id', commentId);

  if (error) throw error;
  return fetchComments(itemId);
}

/**
 * Add a suggestion for an item.
 * Replaces: API.post('suggestions/add/', { item, lat, lng, author, title })
 */
export async function addSuggestion(itemId, lat, lng, title) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: suggestion, error: sugError } = await supabase
    .from('suggestions')
    .insert({
      item_id: itemId,
      author_id: user.id,
      title,
      location: `POINT(${lng} ${lat})`
    })
    .select()
    .single();

  if (sugError) throw sugError;

  // Link to item
  await supabase
    .from('media_items_suggestions')
    .insert({ media_item_id: itemId, suggestion_id: suggestion.id });

  return suggestion;
}

/**
 * Fetch suggestions for an item.
 * Replaces: API.get(`suggestions/byitem/${itemId}/`)
 */
export async function fetchSuggestions(itemId) {
  const data = await readApi('fetch_suggestions', { item_id: itemId });
  return data || [];
}

/**
 * Fetch meta/about information.
 * Replaces: API.get('meta/get/')
 */
export async function fetchMeta() {
  const data = await readApi('fetch_meta');
  return data || { about: '' };
}

/**
 * Add a new media item.
 * Replaces: API.post('items/add/', formData)
 */
export async function addItem(itemData) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Session expired — please log in again');
  }
  const { data, error } = await supabase
    .from('media_items')
    .insert({
      mediatype: itemData.mediatype || 1,
      title: itemData.title,
      year: (COLLECTION === 'scholten' && !itemData.year) ? 1921 : itemData.year,
      year_end: (COLLECTION === 'scholten' && !itemData.year_end) ? 1923 : itemData.year_end,
      desc: itemData.desc,
      source: itemData.source,
      notes: itemData.notes,
      external_id: itemData.external_id,
      image_url: itemData.image_url,
      image: itemData.image,
      angle: itemData.angle,
      collection: COLLECTION || 'scholten',
      location: itemData.lat && itemData.lng
        ? `POINT(${itemData.lng} ${itemData.lat})`
        : null
    })
    .select()
    .single();

  if (error) throw error;

  // Handle authors if provided
  if (itemData.authors && data) {
    for (const authorId of itemData.authors) {
      await supabase
        .from('media_items_authors')
        .insert({ media_item_id: data.id, author_id: authorId });
    }
  }

  return data;
}

/**
 * Fetch beams for an item.
 * Replaces: API.get(`beams/byitem/${itemId}/`)
 */
export async function fetchBeams(itemId) {
  const data = await readApi('fetch_beams', { item_id: itemId });
  return data || [];
}

/**
 * Fetch item detail (external_id) for a single item.
 * Used by modal to show Leiden link without exposing external_id in bulk responses.
 */
export async function getItemDetail(itemId) {
  const data = await readApi('get_item_detail', { item_id: itemId });
  return data || {};
}
